import cn from 'classnames';
import type { SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@sravni/react-design-system/lib/Button';
import { MaskInput } from '@sravni/react-design-system/lib/MaskInput';
import { Text } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';

import type { Winner } from '@src/@types/ozonPrizeWinners';
import {
  DESKTOP_BUTTON_SIZE,
  MAX_MOBILE_NUMBER_CHAR_COUNT,
  MOBILE_BUTTON_SIZE,
  PHONE_INPUT_LABEL,
} from '@src/components/PageHeader/consts';
import { sendCheckPhoneNumberClickEvent } from '@src/helpers/analyticsEvents';

import { WINNER_LIST } from '../../../constants';

import styles from './styles.module.scss';

type Props = {
  setCurrentWinner: React.Dispatch<SetStateAction<Winner | null>>;
  setIsSubmitted: React.Dispatch<SetStateAction<boolean>>;
};

export const PhoneInput: React.FC<Props> = ({ setCurrentWinner, setIsSubmitted }) => {
  const isMobile = useIsMobile();

  const [phone, setPhone] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [focus, setFocus] = useState(false);

  const handleBlur = useCallback(() => {
    setFocus(false);
  }, [setFocus]);

  const handleFocus = useCallback(() => {
    setFocus(true);
  }, [setFocus]);

  const handleChangePhone = useCallback(
    (value: string) => {
      const newValue = value.replace(/[^0-9]/g, '').replace(/^7/, '+7');
      setPhone(newValue);
      setErrorMessage('');
    },
    [setPhone],
  );

  const validatePhoneNumber = useCallback((phoneNumber: string) => {
    const phoneNumberRegex = /^\+79\d{9}$/;
    return phoneNumberRegex.test(phoneNumber);
  }, []);

  const handleSendPhone = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      sendCheckPhoneNumberClickEvent();

      if (phone === '+7' || phone.length === 0) {
        setErrorMessage('Заполните поле');
        return;
      }

      if (phone.length !== MAX_MOBILE_NUMBER_CHAR_COUNT || !validatePhoneNumber(phone)) {
        setErrorMessage('Введите корректный номер телефона');
        return;
      }
      const find = WINNER_LIST.find((winner) => winner.phoneNumber === phone);
      if (find) {
        setIsSubmitted(true);
        setCurrentWinner(find);
        setSuccessMessage('Номер есть в списке победителей');
      } else {
        setErrorMessage('Номера нет в списке победителей');
        setIsSubmitted(true);
        setCurrentWinner(null);
      }
    },
    [phone, setCurrentWinner, setIsSubmitted, validatePhoneNumber],
  );

  // возвращаем список, если номер телефона изменился
  useEffect(() => {
    if (phone.length !== MAX_MOBILE_NUMBER_CHAR_COUNT) {
      setCurrentWinner(null);
      setIsSubmitted(false);
      setSuccessMessage('');
    }
  }, [phone, setCurrentWinner, setIsSubmitted]);

  return (
    <div className={styles.container}>
      <Text size={12} className="h-color-D60">
        Проверьте, стал ли ваш номер выигрышным
      </Text>
      <div className={styles.wrapper}>
        <div className={cn(styles.phoneInput, { [styles.success]: Boolean(successMessage) && !focus })}>
          <MaskInput
            label={PHONE_INPUT_LABEL}
            mask="+7 (999) 999-99-99"
            onChange={handleChangePhone}
            onFocus={handleFocus}
            onBlur={handleBlur}
            value={phone}
            invalid={!focus && Boolean(errorMessage)}
            inputMode="tel"
          />
        </div>
        {Boolean(errorMessage) && !focus && isMobile && (
          <Text className={styles.errorMessage} size={12}>
            {errorMessage}
          </Text>
        )}
        {Boolean(successMessage) && !focus && isMobile && (
          <Text className={styles.successMessage} size={12}>
            {successMessage}
          </Text>
        )}
        <Button
          variant="primary"
          size={isMobile ? MOBILE_BUTTON_SIZE : DESKTOP_BUTTON_SIZE}
          onClick={handleSendPhone}
          className={styles.button}
        >
          Проверить
        </Button>
      </div>
      {Boolean(errorMessage) && !focus && !isMobile && (
        <Text className={styles.errorMessage} size={12}>
          {errorMessage}
        </Text>
      )}
      {Boolean(successMessage) && !focus && !isMobile && (
        <Text className={styles.successMessage} size={12}>
          {successMessage}
        </Text>
      )}
    </div>
  );
};
